import {
  LearnerClassroomAssignmentClassPostsBySectionQuery,
  ThreadDetailQuery,
} from "@outschool/gql-frontend-generated";

import { VIEW } from "./constants";
import {
  BubbleMessageData,
  ConversationListItem,
  DrawerView,
  LearnerInfo,
  PostTarget,
  PrivateThreadItem,
  ThreadLike,
} from "./types";

type ThreadMessageData = ThreadDetailQuery["thread"]["messages"][number];
type AssignmentCommentData = NonNullable<
  LearnerClassroomAssignmentClassPostsBySectionQuery["section"]
>["assignmentClassPosts"][number]["comments"][number];

export function toBubbleMessageData(
  message: ThreadMessageData,
): BubbleMessageData {
  return {
    uid: message.uid,
    sentAt: message.sentAt,
    sender: {
      uid: message.sender.uid,
      name: message.sender.name,
      photo: message.sender.photo,
      isLearner: message.sender.isLearner,
    },
    messageContent: {
      uid: message.messageContent.uid,
      text: message.messageContent.text,
      attachments: message.messageContent.attachments ?? [],
      video: message.messageContent.video ?? null,
    },
  };
}

export function toBubbleMessageFromAssignmentComment(
  message: AssignmentCommentData,
): BubbleMessageData {
  return {
    uid: message.uid,
    sentAt: message.sentAt,
    sender: {
      uid: message.sender.uid,
      name: message.sender.name,
      photo: message.sender.photo,
      avatar: message.sender.avatar,
      isLearner: message.sender.isLearner,
    },
    messageContent: {
      uid: message.messageContent.uid,
      text: message.messageContent.text,
      attachments: message.messageContent.attachments ?? [],
      video: message.messageContent.video ?? null,
    },
  };
}

export function isGeneralThread(thread: ThreadLike): boolean {
  return !thread?.classPost && !thread?.learningAssignmentClassPost;
}

export function getThreadPostTitle(thread: ThreadLike): string | null {
  return (
    thread?.classPost?.title ??
    thread?.learningAssignmentClassPost?.title ??
    null
  );
}

export function getPostTarget(props: PostTarget): PostTarget {
  if (props.classPostUid) {
    return { classPostUid: props.classPostUid };
  }
  if (props.learningAssignmentClassPostUid) {
    return {
      learningAssignmentClassPostUid: props.learningAssignmentClassPostUid,
    };
  }
  return {};
}

export function getThreadPostTarget(thread: ThreadLike): PostTarget {
  if (thread?.classPost?.uid) {
    return { classPostUid: thread.classPost.uid };
  }
  if (thread?.learningAssignmentClassPost?.uid) {
    return {
      learningAssignmentClassPostUid: thread.learningAssignmentClassPost.uid,
    };
  }
  return {};
}

export function isFeedbackConversation(
  item: ConversationListItem,
): item is Extract<ConversationListItem, { kind: "feedback" }> {
  return item.kind === "feedback";
}

export function toConversationListItem(
  thread: PrivateThreadItem["thread"],
): PrivateThreadItem {
  return { kind: "thread", thread };
}

export function getConversationLatestMessage(
  item: ConversationListItem,
): BubbleMessageData | PrivateThreadItem["thread"]["latestMessage"] | null {
  return item.kind === "thread"
    ? item.thread.latestMessage
    : item.latestMessage;
}

export function getConversationKey(item: ConversationListItem): string {
  return item.kind === "thread" ? item.thread.uid : item.uid;
}

export function getConversationTimestamp(
  item: ConversationListItem,
): string | null {
  return getConversationLatestMessage(item)?.sentAt ?? null;
}

export function getConversationTitle(
  item: ConversationListItem,
  teacherName: string,
  t: (key: string, params?: Record<string, string>) => string,
): string {
  const normalizedTeacherName = teacherName?.trim() || t("Teacher");
  if (item.kind === "feedback") {
    return t("Feedback: {{assignmentTitle}}", {
      assignmentTitle: item.assignmentTitle,
    });
  }
  return (
    getThreadPostTitle(item.thread) ??
    t("Ask {{teacherName}} anything", { teacherName: normalizedTeacherName })
  );
}

export function sortConversationsByLatestSentAt(
  items: ConversationListItem[],
): ConversationListItem[] {
  return [...items].sort((a, b) => {
    const aSentAt = getConversationTimestamp(a);
    const bSentAt = getConversationTimestamp(b);
    if (!aSentAt && !bSentAt) {
      return 0;
    }
    if (!aSentAt) {
      return 1;
    }
    if (!bSentAt) {
      return -1;
    }
    return bSentAt.localeCompare(aSentAt);
  });
}

export function getConversationDetailView(
  item: ConversationListItem,
): DrawerView {
  return item.kind === "thread"
    ? {
        kind: VIEW.THREAD,
        threadUid: item.thread.uid,
        postTitleOverride: null,
      }
    : {
        kind: VIEW.FEEDBACK,
        feedbackThreadUid: item.uid,
      };
}

export function buildLearnerSender(learner: LearnerInfo) {
  return {
    uid: learner.uid,
    name: learner.name ?? "",
    photo: null,
    isLearner: true as const,
  };
}
